import { useCallback, useEffect, useRef, useState } from "react";

import { ICardFeePreview } from "./PaymentFees";

/**
 * CLE-2798 — reads one card in the background so its surcharge is on screen before the patient presses
 * Pay. `enabled` turns true once the card's fields are all valid (or a saved card is selected); an edit
 * makes the fields incomplete and then complete again, which re-reads the card. `sourceKey` re-reads
 * when the card source changes while staying enabled. A read that fails shows nothing: Pay reads the
 * card again and reports any problem. `cancel` drops a read still in flight when Pay is pressed.
 */
export function useCardFeePreview({
  enabled,
  sourceKey,
  read,
  delayMs = 500,
}: {
  enabled: boolean;
  sourceKey: string;
  read: () => Promise<ICardFeePreview | null>;
  delayMs?: number;
}) {
  const [preview, setPreview] = useState<ICardFeePreview | null>(null);
  const [loading, setLoading] = useState(false);
  const runRef = useRef(0);
  const readRef = useRef(read);
  readRef.current = read;

  useEffect(() => {
    runRef.current += 1;
    const runId = runRef.current;
    setPreview(null);
    setLoading(false);
    if (!enabled) return undefined;

    const timer = setTimeout(async () => {
      if (runId !== runRef.current) return;
      setLoading(true);
      try {
        const result = await readRef.current();
        if (runId === runRef.current) setPreview(result);
      } catch {
        // Nothing to show.
      } finally {
        if (runId === runRef.current) setLoading(false);
      }
    }, delayMs);
    return () => clearTimeout(timer);
  }, [enabled, sourceKey, delayMs]);

  const cancel = useCallback(() => {
    runRef.current += 1;
    setLoading(false);
  }, []);

  return { preview, loading, cancel };
}
